/**
 * Rolling buffer of recent client-side errors, attached automatically to
 * issue reports so the triage agent can cross-reference what the browser saw
 * with the server logs. Captures console.error, uncaught errors and
 * unhandled promise rejections. Nothing leaves the browser until the user
 * explicitly submits a report.
 */

export interface ClientErrorEntry {
  ts: string;
  message: string;
}

const MAX_ENTRIES = 50;
const buffer: ClientErrorEntry[] = [];

function push(message: string) {
  buffer.push({ ts: new Date().toISOString(), message: message.slice(0, 500) });
  if (buffer.length > MAX_ENTRIES) buffer.splice(0, buffer.length - MAX_ENTRIES);
}

function stringify(args: unknown[]): string {
  return args
    .map((a) => {
      if (a instanceof Error) return `${a.name}: ${a.message}`;
      if (typeof a === 'string') return a;
      try { return JSON.stringify(a); } catch { return String(a); }
    })
    .join(' ');
}

let installed = false;

export function installClientErrorBuffer(): void {
  if (installed) return;
  installed = true;

  const origError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try { push(stringify(args)); } catch { /* never break console */ }
    origError(...args);
  };

  window.addEventListener('error', (e) => {
    push(`Uncaught: ${e.message} (${e.filename}:${e.lineno})`);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason instanceof Error ? `${e.reason.name}: ${e.reason.message}` : String(e.reason);
    push(`Unhandled rejection: ${reason}`);
  });
}

export function recentClientErrors(): ClientErrorEntry[] {
  return [...buffer];
}
